/**
 * useServerDataTable - Hook for server-side data tables with TanStack Table + TanStack Query
 *
 * MIGRADO A TANSTACK QUERY para aprovechar:
 * - Cache automático: datos se cachean por queryKey + parámetros
 * - Deduplicación: múltiples componentes con mismos params = 1 request
 * - Background refetch: datos se actualizan automáticamente cuando vuelven a ser "stale"
 * - Optimistic updates: mejor UX durante paginación
 *
 * Encapsulates common logic for:
 * - Server-side pagination, sorting, and filtering
 * - URL state synchronization
 * - localStorage persistence for page size
 * - Loading and error states
 * - Automatic refetch on filter changes
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type SortingState,
  type PaginationState,
  type Table,
} from "@tanstack/react-table";
import { useDebounce } from "./use-debounce";

// Generic paginated response from API
export interface PaginatedResponse<T> {
  results: T[];
  count: number;
  next?: string | null;
  previous?: string | null;
}

// Parameters sent to the fetch function
export interface FetchParams {
  page: number;
  page_size: number;
  ordering?: string;
  search?: string;
  [key: string]: string | number | boolean | undefined;
}

// Filter configuration
export interface FilterConfig {
  key: string;
  defaultValue: string;
  urlParam?: string; // defaults to key if not provided
}

// Hook configuration
export interface UseServerDataTableOptions<TData> {
  columns: ColumnDef<TData>[];
  fetchFn: (params: FetchParams) => Promise<PaginatedResponse<TData>>;
  queryKey: string; // REQUIRED: base key for TanStack Query cache
  storageKey?: string; // for localStorage page size persistence
  defaultPageSize?: number;
  debounceMs?: number;
  filters?: FilterConfig[];
  initialFilters?: Record<string, string>;
  staleTime?: number; // how long data is considered fresh (default: 30s)
  enabled?: boolean; // whether to enable the query (default: true)
}

// Hook return type
export interface UseServerDataTableReturn<TData> {
  table: Table<TData>;
  data: TData[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  refetch: () => Promise<void>;
  // Filter management
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  filters: Record<string, string>;
  setFilter: (key: string, value: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

export function useServerDataTable<TData>({
  columns,
  fetchFn,
  queryKey,
  storageKey = "table_page_size",
  defaultPageSize = 10,
  debounceMs = 300,
  filters: filterConfigs = [],
  initialFilters = {},
  staleTime = 30000, // 30 seconds default
  enabled = true, // default to enabled
}: UseServerDataTableOptions<TData>): UseServerDataTableReturn<TData> {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Pagination state (TanStack Table uses 0-indexed pages)
  const [pagination, setPagination] = useState<PaginationState>(() => {
    const page = searchParams.get("page");
    const urlSize = searchParams.get("page_size");
    const savedSize = localStorage.getItem(storageKey);
    return {
      pageIndex: page ? parseInt(page, 10) - 1 : 0,
      pageSize: urlSize
        ? parseInt(urlSize, 10)
        : savedSize
          ? parseInt(savedSize, 10)
          : defaultPageSize,
    };
  });

  // Sorting state
  const [sorting, setSorting] = useState<SortingState>(() => {
    const sortField = searchParams.get("sort");
    const sortDir = searchParams.get("dir");
    if (sortField && sortDir) {
      return [{ id: sortField, desc: sortDir === "desc" }];
    }
    return [];
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const debouncedSearchTerm = useDebounce(searchTerm, debounceMs);

  // Dynamic filters state
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    filterConfigs.forEach((config) => {
      const urlParam = config.urlParam || config.key;
      const urlValue = searchParams.get(urlParam);
      initial[config.key] = urlValue || initialFilters[config.key] || config.defaultValue;
    });
    return initial;
  });

  // Track initial mount to avoid resetting pagination
  const isInitialMount = useRef(true);
  const prevFilters = useRef<Record<string, string>>({ debouncedSearchTerm, ...filters });

  // Stable reference for filterConfigs
  const filterConfigsRef = useRef(filterConfigs);
  filterConfigsRef.current = filterConfigs;

  // Build query params for TanStack Query
  const buildQueryParams = useCallback((): FetchParams => {
    let ordering: string | undefined;
    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      ordering = desc ? `-${id}` : id;
    }

    const params: FetchParams = {
      page: pagination.pageIndex + 1,
      page_size: pagination.pageSize,
      ordering,
      search: debouncedSearchTerm || undefined,
    };

    // Add dynamic filters (only if not default value)
    filterConfigsRef.current.forEach((config) => {
      const value = filters[config.key];
      if (value !== config.defaultValue) {
        params[config.key] = value;
      }
    });

    return params;
  }, [pagination.pageIndex, pagination.pageSize, debouncedSearchTerm, sorting, filters]);

  // Query params for cache key
  const queryParams = buildQueryParams();

  // TanStack Query for data fetching
  const {
    data: queryData,
    isLoading,
    error: queryError,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: [queryKey, queryParams],
    queryFn: () => fetchFn(queryParams),
    enabled, // only fetch if enabled
    staleTime,
    // Re-fetch when component mounts if data is stale (e.g. navigating back after a mutation)
    refetchOnMount: true,
    // Keep previous data while fetching new page (smoother pagination UX)
    placeholderData: (previousData) => previousData,
  });

  // Extract data from query result
  const data = queryData?.results ?? [];
  const totalCount = queryData?.count ?? 0;
  const error = queryError
    ? queryError instanceof Error
      ? queryError.message
      : "Error loading data"
    : null;

  // Set a single filter
  const setFilter = useCallback((key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setSorting([]);
    const clearedFilters: Record<string, string> = {};
    filterConfigs.forEach((config) => {
      clearedFilters[config.key] = config.defaultValue;
    });
    setFilters(clearedFilters);
  }, [filterConfigs]);

  // Check if any filter is active
  const hasActiveFilters =
    searchTerm !== "" ||
    sorting.length > 0 ||
    filterConfigs.some((config) => filters[config.key] !== config.defaultValue);

  // Page count for table
  const pageCount = Math.ceil(totalCount / pagination.pageSize);

  // TanStack Table instance
  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === "function" ? updater(sorting) : updater;
      setSorting(newSorting);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === "function" ? updater(pagination) : updater;
      setPagination(newPagination);
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  // Refetch function that invalidates cache
  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [queryKey] });
    await queryRefetch();
  }, [queryClient, queryKey, queryRefetch]);

  // Reset to page 0 when filters change (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const currentFilters: Record<string, string> = { debouncedSearchTerm, ...filters };
    const filtersChanged = Object.keys(currentFilters).some(
      (key) => prevFilters.current[key] !== currentFilters[key]
    );

    if (filtersChanged && pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }

    prevFilters.current = currentFilters;
  }, [debouncedSearchTerm, filters, pagination.pageIndex]);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();

    if (searchTerm) params.set("search", searchTerm);
    if (pagination.pageIndex > 0) params.set("page", String(pagination.pageIndex + 1));
    if (pagination.pageSize !== defaultPageSize)
      params.set("page_size", String(pagination.pageSize));
    if (sorting.length > 0) {
      params.set("sort", sorting[0].id);
      params.set("dir", sorting[0].desc ? "desc" : "asc");
    }

    // Add dynamic filters to URL
    filterConfigsRef.current.forEach((config) => {
      const value = filters[config.key];
      if (value !== config.defaultValue) {
        const urlParam = config.urlParam || config.key;
        params.set(urlParam, value);
      }
    });

    setSearchParams(params, { replace: true });
  }, [searchTerm, pagination, sorting, filters, defaultPageSize, setSearchParams]);

  // Save pageSize to localStorage
  useEffect(() => {
    localStorage.setItem(storageKey, String(pagination.pageSize));
  }, [pagination.pageSize, storageKey]);

  return {
    table,
    data,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  };
}
